/**
 * Shared helpers for the nurse note standing order dropdown.
 *
 * The medication list itself is owned by the backend - a fixed set of constants served by
 * GET /api/medication/standing-order - so it is never duplicated here. Whether a note has been signed off
 * is read from the structured standing_order_* fields on the progress note response, not by searching the
 * note text.
 */
app.service("standingOrderService", [
	"$filter",
	"Session",
	function ($filter, Session) {
		// Mirrors max-length on the free_text richtext-editor in progressNoteForm.html.
		const FREE_TEXT_MAX_LENGTH = 3000;

		// Escapes by round-tripping through textContent, so a medication name or a user's surname
		// containing & < > cannot inject markup into the note body.
		function escapeHtml(value) {
			const div = document.createElement("div");
			div.textContent = (value === null || value === undefined) ? "" : String(value);
			return div.innerHTML;
		}

		// Regex rather than DOM parsing: we only need a rough plain-text length to enforce the editor's
		// max-length, and untrusted note HTML must not be round-tripped through innerHTML to read it.
		function toPlainText(html) {
			return String(html || "")
				.replace(/<br\s*\/?>/gi, " ")
				.replace(/<\/(p|div|li|h[1-6])>/gi, " ")
				.replace(/<[^>]*>/g, "")
				.replace(/&nbsp;/gi, " ")
				.replace(/&amp;/gi, "&")
				.replace(/\s+/g, " ")
				.trim();
		}

		// Mirrors nz.co.cattrax.domain.enums.Eye. Labels and OD/OU/OS ordering follow the convention already
		// used by the operative-eye radios in this same form.
		const LATERALITY_OPTIONS = [
			{ value: "OD", name: "Right eye (OD)" },
			{ value: "OU", name: "Both eyes" },
			{ value: "OS", name: "Left eye (OS)" },
		];

		function lateralityLabel(laterality) {
			const match = LATERALITY_OPTIONS.filter(function (option) {
				return option.value === laterality;
			})[0];
			return match ? match.name : "";
		}

		var service = {
			FREE_TEXT_MAX_LENGTH: FREE_TEXT_MAX_LENGTH,
			LATERALITY_OPTIONS: LATERALITY_OPTIONS,

			// Keeps the rest of the app insulated from the exact response field names.
			//
			// A standing order medication is identified by its name alone. The list is a fixed set of backend
			// constants carrying no id, and the name is what gets denormalised onto the note, so there is
			// nothing else to key on.
			normaliseOptions: function (payload) {
				return (payload || [])
					.map(function (medication) {
						return {
							name: medication.medication,
							requiresLaterality: !!medication.standing_order_requires_laterality,
						};
					})
					.filter(function (medication) {
						return !!medication.name;
					});
			},

			// True while the nurse still has to say which eye before this order can be recorded. An ocular
			// medication with no eye is not a valid clinical record, so the form must not let it be added.
			isLateralityMissing: function (medication, laterality) {
				return !!medication && medication.requiresLaterality && !laterality;
			},

			// Ocular:  "Standing order: g. Tropicamide 1%, Right eye (OD) - administered by Jane Smith on 11/08/2026"
			// General: "Standing order: Paracetamol 1gm po stat - administered by Jane Smith on 11/08/2026"
			//
			// The medication name is already a complete prescription (form prefix and strength included), so
			// nothing else needs appending. Laterality sits immediately after it: which eye was treated is
			// the most clinically significant part of the entry and should not be buried at the end.
			buildAdministeredLine: function (medication, laterality) {
				const nurseName = [Session.user.firstName, Session.user.lastName]
					.filter(Boolean)
					.join(" ")
					.trim();
				const today = $filter("date")(new Date(), "dd/MM/yyyy");

				let line = "Standing order: " + medication.name;
				if (laterality) {
					line += ", " + lateralityLabel(laterality);
				}
				line += " - administered by " + nurseName + " on " + today;

				return line;
			},

			// Its own paragraph so it reads as a discrete record entry, and so Quill keeps it as one block.
			buildAdministeredHtml: function (medication, laterality) {
				return "<p>" + escapeHtml(service.buildAdministeredLine(medication, laterality)) + "</p>";
			},

			plainTextLength: function (html) {
				return toPlainText(html).length;
			},
		};

		return service;
	},
]);
